import { useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { useAssignConversation } from '../../hooks/use-assign-conversation';
import { useAssignableConversations } from '../../hooks/use-assignable-conversations';

import { ModalBase } from './ModalBase';

import type { AttendantVM } from '../../view-models';
import type React from 'react';

// Item 335 — modal de "Atribuir conversa": lista as conversas SEM DONO do escopo
// do supervisor (`teamQueue=mine` + `assignedToUserId=unassigned`) e atribui a
// conversa escolhida ao atendente do card. Gate de cap: conversation-assign.
export function AssignConversationModal({
  attendant,
  onClose,
}: {
  attendant: AttendantVM;
  onClose: () => void;
}): React.JSX.Element | null {
  const canAssign = useCapability('conversation-assign');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  // Gatear o fetch (não só o render): a query só dispara com a cap concedida.
  const { data, isLoading, isError } = useAssignableConversations(canAssign);
  const assign = useAssignConversation();

  if (!canAssign) return null;

  const conversations = data?.data ?? [];
  const disabled = selectedId === null || assign.isPending;

  const handleConfirm = (): void => {
    if (selectedId === null) return;
    assign.mutate(
      {
        conversationId: selectedId,
        assignToUserId: attendant.id,
        ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <ModalBase
      open
      onClose={onClose}
      title={`Atribuir conversa a ${attendant.name}`}
      width={580}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button type="button" className="btn-ghost-mini" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-ghost-mini btn-ghost-mini--primary"
            disabled={disabled}
            onClick={handleConfirm}
          >
            {assign.isPending ? 'Atribuindo…' : 'Atribuir'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <div className="modal-form__callout">
          Selecione uma conversa sem dono para atribuir a <b>{attendant.name}</b>.
        </div>

        {isLoading && <div aria-busy="true">Carregando conversas…</div>}
        {isError && !isLoading && (
          <div role="alert">Não foi possível carregar as conversas. Tente novamente.</div>
        )}
        {!isLoading && !isError && conversations.length === 0 && (
          <div className="modal-form__field">Nenhuma conversa sem dono no seu escopo agora.</div>
        )}

        {!isLoading && !isError && conversations.length > 0 && (
          <fieldset className="modal-form__fieldset">
            <legend>Conversas sem dono ({conversations.length})</legend>
            {conversations.map((c) => (
              <label key={c.id} className="modal-form__check">
                <input
                  type="radio"
                  name="assign-conversation"
                  value={c.id}
                  checked={selectedId === c.id}
                  onChange={() => {
                    setSelectedId(c.id);
                  }}
                />{' '}
                <span>
                  <b>{c.person?.displayName ?? 'Sem nome'}</b>
                  {c.lastMessage?.content !== undefined && c.lastMessage.content !== '' && (
                    <>
                      {' · '}
                      {c.lastMessage.content}
                    </>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
        )}

        <label className="modal-form__field">
          <span className="modal-form__label">Motivo (opcional)</span>
          <textarea
            className="modal-form__textarea"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={3}
            maxLength={1000}
            placeholder="Ex.: cliente recorrente do João; conversa parada na fila aberta."
          />
        </label>

        {assign.isError && (
          <div role="alert" className="modal-form__callout">
            Não foi possível atribuir a conversa. Tente novamente.
          </div>
        )}
      </div>
    </ModalBase>
  );
}
